<template>
  <div
    class="input"
    :class="{
      'input--focused': isFocus,
      'input--disabled': props.isDisabled,
    }"
    @click="clickFocusOutside"
  >
    <div
      class="input-container"
      :class="{
        'input-container--focused': isFocus,
        'input-container--with-value': haveValue,
        'input-container--error': props.isWithError,
        'input-container--success': props.isWithSuccess,
        'input-container--small': props.isSmallSize,
        'input-container--micro': props.isMicro,
        'input-container--disabled': props.isDisabled,
      }"
    >
      <div class="input-container__left-slot" v-if="isLeftInputAddons">
        <slot name="leftInputAddons"></slot>
      </div>
      <div class="input-container__input-wrapper">
        <span :class="['input-container__input-label', { 'is-mask-input': mask && !mask?.lazy }]">{{
          props.label
        }}</span>
        <input
          v-if="mask"
          :style="{ color: props.color, backgroundColor: `${props.color + '1A'}` }"
          class="input-container__input"
          :type="props.type"
          :maxlength="props.maxLength"
          :placeholder="props.placeholder"
          @focus="onFocus"
          @blur="onBlur"
          @keyup.enter="onEnter"
          :disabled="props.isDisabled"
          ref="currentInput"
          name=""
          tabindex="0"
          :value="value"
          :autocomplete="computedIsAutocomplete"
          v-mask="mask"
          @accept="emit('acceptMask', $event)"
          @complete="emit('completeMask', $event)"
        />
        <input
          v-else
          :style="{ color: props.color, backgroundColor: `${props.color + '1A'}` }"
          class="input-container__input"
          :type="props.type"
          :maxlength="props.maxLength"
          :placeholder="props.placeholder"
          @focus="onFocus"
          @blur="onBlur"
          @keyup.enter="onEnter"
          :disabled="props.isDisabled"
          ref="currentInput"
          name=""
          tabindex="0"
          :value="value"
          @input="onInput"
          :autocomplete="computedIsAutocomplete"
        />
      </div>
      <div class="input-container__right-slot" v-if="isRightInputAddons">
        <slot name="rightInputAddons"></slot>
      </div>
      <input
        type="range"
        :min="props.minSliderValue"
        :max="props.maxSliderValue"
        :step="localRangeComputed"
        ref="inputSlider"
        @input="onInputSlider"
        class="input-range-slider"
        :class="{
          'input-range-slider--error': props.isWithError,
        }"
        :style="{
          background: `linear-gradient(to right, currentColor ${
            Math.round((Number(value) * 100) / props.maxSliderValue / 10) * 10
          }%, transparent 0%)`,
        }"
        :value="props.modelValue"
        v-if="props.type == 'number' && props.isShowRangeLine"
      />
    </div>
    <div
      class="input-range-slider-signs"
      :class="{
        'input-range-slider-signs--error': props.isWithError,
      }"
      v-if="props.type == 'number' && props.isShowRangeLine"
    >
      <div v-for="num of labels" :key="num">
        {{ num }}
      </div>
    </div>

    <div
      class="input__hint-message"
      v-if="props.isWithHint"
      :class="{
        'input__hint-message--error': props.isWithError,
        'input__hint-message--show': props.isShowHint,
        'input__hint-message--success': props.isWithSuccess,
      }"
    >
      <slot name="hintMessage"></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { PropType, computed, onMounted, ref, useSlots, defineEmits } from "vue";
import { IMaskDirective as vMask } from "tir-pm-mask/vue-imask";

const props = defineProps({
  /**
   * Показывать ли линейку range значений
   */
  isShowRangeLine: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**
   * Значение
   */
  modelValue: {
    type: [String, Number] as PropType<string | number>,
    default: null,
  },
  /**
   * Максимальное значение для слайдера
   */
  maxSliderValue: {
    type: Number as PropType<number>,
    default: 120,
  },
  /**
   * Минимальное значение для слайдера
   */
  minSliderValue: {
    type: Number as PropType<number>,
    default: 0,
  },
  /**
   * Разбег значений для подписей к слайдеру
   */
  rangeSignsSliderValue: {
    type: Number as PropType<number>,
    default: 10,
  },
  /**
   * Лэйбл
   */
  label: {
    type: String as PropType<string>,
    default: "",
  },
  /**
   * Цвет
   */
  color: {
    type: String as PropType<string>,
  },
  /**
   * Заголовок
   */
  title: {
    type: String as PropType<string>,
    default: "",
  },
  /**
   * Нужно ли автозаполнение
   */
  isAutocomplete: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**
   * Плейсхолдер
   */
  placeholder: {
    type: String as PropType<string>,
    default: "",
  },
  /**
   * Показывать ли подсказку всегда
   */
  isShowHintDefault: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**
   * Показывать ли подсказку в данный момент
   */
  isShowHint: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**
   * Показывать ли подсказку вообще, есть ли она, делать ли зарезервированное снизу свободное пространство
   */
  isWithHint: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /**
   * Ошибка
   */
  isWithError: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**
   * Успешное состояние
   */
  isWithSuccess: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**
   * Дизейбл
   */
  isDisabled: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**
   * Тип
   */
  type: {
    type: String as PropType<string>,
    default: "text",
  },
  /**
   * Макс длина
   */
  maxLength: {
    type: String as PropType<string>,
    default: undefined,
  },
  /**
   * Среднй
   */
  isSmallSize: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**
   * Маленький
   */
  isMicro: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**Автофокус */
  autoFocus: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**Автовыделение всего контента в инпуте */
  autoSelect: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /**Маска */
  mask: {
    type: Object,
  },
});

/**
 * Вычисляет, нужно ли автозаполнение
 */
const computedIsAutocomplete = computed(() => (props.isAutocomplete ? "on" : "off"));

/**
 * Внутреннее значение
 */
const innerValue = ref<string | number>("");
/**
 * Значение
 */
const value = computed({
  get: () => {
    return props.modelValue == null ? innerValue.value : props.modelValue;
  },
  set: (value) => {
    emit("update:modelValue", value);
    innerValue.value = value;
  },
});
/**Есть ли значение */
const haveValue = computed(() => {
  return !!value.value;
});
/**
 * Элемент салйдера
 */
const inputSlider = ref<HTMLInputElement>();

const htmlElement = document.documentElement;

const rootFontSizePx = parseInt(getComputedStyle(htmlElement).fontSize);

const onInput = (e: InputEvent) => {
  emit("input", (e.target as HTMLInputElement).value);
  value.value = (e.target as HTMLInputElement).value;
};
const onInputSlider = (e: Event) => {
  emit("input", (e.target as HTMLInputElement).value);
  value.value = (e.target as HTMLInputElement).value;
};

const focus = () => {
  currentInput.value?.focus();
};

const select = () => {
  currentInput.value?.select();
};

const checkAutofocusOrAutoselect = () => {
  if (!currentInput.value) return;

  if (props.autoSelect) {
    select();
    return;
  }

  if (props.autoFocus) {
    focus();
    return;
  }
};

let labels = ref<Array<number | string>>([]);
const localRangeComputed = ref(0);
onMounted(() => {
  if (props.type == "number") {
    localRangeComputed.value = props.maxSliderValue / props.rangeSignsSliderValue;
    if (props.minSliderValue == localRangeComputed.value * 1) {
      labels.value.push(0);
    } else {
      labels.value.push(props.minSliderValue);
    }
    for (let index = 1; index <= props.rangeSignsSliderValue; index++) {
      labels.value.push((localRangeComputed.value * index).toFixed());
    }
    console.log(props.modelValue);
    if (typeof props.modelValue == "number" && props.modelValue) {
      if (inputSlider.value) {
        inputSlider.value.value = props.modelValue.toString();
      }
    } else {
      if (inputSlider.value) {
        inputSlider.value.value = props.minSliderValue.toString();
      }
    }
  }

  checkAutofocusOrAutoselect();
});
/**
 * События
 */

const emit = defineEmits<{
  (eventName: "update:modelValue", value: string | number): void;
  (eventName: "focus", value: boolean): void;
  (eventName: "blur", value: boolean): void;
  (eventName: "enter", value: boolean): void;
  (eventName: "input", value: string | number): void;
  (eventName: "acceptMask", value: CustomEvent): void;
  (eventName: "completeMask", value: CustomEvent): void;
}>();
/**
 * Инпут
 */
const currentInput = ref<HTMLInputElement>();
/**
 * Фокус
 */
const isFocus = ref(false);
/**
 * При кике вне элемента инпута
 */
const clickFocusOutside = () => {
  if (props.isDisabled && isFocus.value) return;
  if (currentInput.value) {
    currentInput.value.focus();
  }
};
/**
 * При фокусе
 */
const onFocus = () => {
  if (props.isDisabled) return;
  isFocus.value = true;
  emit("focus", isFocus.value);
};
/**
 * При блюре
 */
const onBlur = () => {
  if (props.isDisabled) return;
  isFocus.value = false;
  emit("blur", isFocus.value);
};

/**
 * При нажатии на клавишу энтер
 */
const onEnter = () => {
  if (props.isDisabled) return;
  isFocus.value = false;
  emit("enter", isFocus.value);
};

/**
 * Столы
 */
const slots = useSlots();
/**
 * Если ли кастомный контент слева
 */
const isLeftInputAddons = computed(() => slots && slots.leftInputAddons && slots.leftInputAddons());
/**
 * Если ли кастомный контент справа
 */
const isRightInputAddons = computed(
  () => slots && slots.rightInputAddons && slots.rightInputAddons(),
);

defineExpose({
  focus,
  select,
});
</script>

<style lang="scss">
@import "./style.scss";
</style>
